import { extractTextFromPdf } from "@/shared/utils/pdfTextExtraction";
import { convertImageFileToPdf } from "@/shared/utils/convertImageFileToPdf";
import { supabase } from "@/shared/lib/supabase";

const MAX_FILE_SIZE_BYTES = 200 * 1024 * 1024; // 200MB

export const DOCUMENT_LIMIT_MESSAGE =
  "You've reached your free plan document limit. Upgrade to upload more.";

export class DocumentLimitError extends Error {
  constructor(message = DOCUMENT_LIMIT_MESSAGE) {
    super(message);
    this.name = "DocumentLimitError";
  }
}

async function computeFileHash(file: File): Promise<string> {
  const buffer = await file.arrayBuffer();
  const hashBuffer = await crypto.subtle.digest("SHA-256", buffer);
  return Array.from(new Uint8Array(hashBuffer))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

export async function uploadDocument(
  file: File,
  documentType: string,
  estateId: number,
): Promise<number> {
  console.log("[uploadDocument] called:", file.name, documentType, estateId);
  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (!session) throw new Error("Not authenticated");

  const accessToken = session.access_token;
  let fileForUpload = file;
  let fileName = file.name;
  let contentType = file.type;

  if (contentType.startsWith("image/")) {
    const converted = await convertImageFileToPdf(file);
    fileForUpload = converted;
    fileName = converted.name;
    contentType = "application/pdf";
  }

  let extractedText: string | undefined = undefined;
  if (contentType === "application/pdf") {
    try {
      extractedText = (await extractTextFromPdf(fileForUpload)) ?? undefined;
    } catch {
      // Password-protected or corrupted PDF — Reducto will handle OCR server-side
    }
  }

  const headers = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`,
    apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
  };

  // File size check
  if (fileForUpload.size > MAX_FILE_SIZE_BYTES) {
    throw new Error(
      `File too large (${Math.round(fileForUpload.size / 1024 / 1024)}MB). Maximum size is 200MB.`,
    );
  }

  // Deduplication check — hash the original file, not the converted PDF,
  // since image-to-PDF conversion produces different bytes each time
  const fileHash = await computeFileHash(file);
  let duplicateId: number | null = null;
  let fileKey: string | undefined;
  let resolvedDocumentType = documentType;

  const dupRes = await fetch(
    `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/documents/${estateId}/check-duplicate`,
    {
      method: "POST",
      headers,
      body: JSON.stringify({
        file_hash: fileHash,
        file_size: file.size,
        estate_id: estateId,
      }),
    },
  );

  if (dupRes.ok) {
    const dupData = await dupRes.json();
    if (dupData.is_duplicate) {
      duplicateId = dupData.duplicate_id;
      fileKey = dupData.document_key;
      if (dupData.document_type) resolvedDocumentType = dupData.document_type;
    }
  }

  // Skip S3 upload if duplicate
  if (!duplicateId) {
    const presignedRes = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/documents/${estateId}/presigned-url`,
      {
        method: "POST",
        headers,
        body: JSON.stringify({ file_name: fileName }),
      },
    );
    if (!presignedRes.ok) {
      if (presignedRes.status === 403) {
        throw new DocumentLimitError();
      }
      throw new Error("Failed to get upload URL");
    }
    const presignedData = await presignedRes.json();
    fileKey = presignedData.file_key;

    const uploadRes = await fetch(presignedData.upload_url, {
      method: "PUT",
      body: fileForUpload,
      headers: { "Content-Type": contentType },
    });
    if (!uploadRes.ok) throw new Error("Failed to upload file");
  }

  const metadataRes = await fetch(
    `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/documents/${estateId}/metadata`,
    {
      method: "POST",
      headers,
      body: JSON.stringify({
        file_key: fileKey,
        file_name: fileName,
        document_type: resolvedDocumentType,
        extracted_text: extractedText,
        file_hash: fileHash,
        file_size: file.size,
        ...(duplicateId && { duplicate_id: duplicateId }),
      }),
    },
  );
  if (!metadataRes.ok) {
    if (metadataRes.status === 403) {
      throw new DocumentLimitError(
        "You've reached your free plan document limit. Upgrade to upload more.",
      );
    }
    throw new Error("Failed to save document");
  }
  const metadataData = await metadataRes.json();
  const docId = metadataData?.document?.id;
  if (typeof docId !== "number") throw new Error("Missing document ID in metadata response");
  return docId;
}
